import React, { useEffect, useState } from "react";
import { ComboBox, IComboBoxOption } from "@fluentui/react";
import { getUsersByCustomerId, CustomerUser } from "@/api/users";

interface UserComboBoxProps {
  customerId?: number;
  selectedUserId?: number;
  onChange: (userId: number | undefined) => void;
  disabled?: boolean;
  label?: string;
  placeholder?: string;
  errorMessage?: string;
}

const userCache = new Map<number, CustomerUser[]>();

const UserComboBox = (props: UserComboBoxProps) => {
  const [options, setOptions] = useState<IComboBoxOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!props.customerId) {
      setOptions([]);
      return;
    }

    const customerId = props.customerId;
    const cached = userCache.get(customerId);
    if (cached) {
      setOptions(
        cached.map((u) => ({
          key: u.Id,
          text: u.EmailAddress,
        }))
      );
      return;
    }

    setLoading(true);
    getUsersByCustomerId(customerId)
      .then((users) => {
        const data = (users || []).filter((u) => u.IsActive);
        userCache.set(customerId, data);
        setOptions(
          data.map((u) => ({
            key: u.Id,
            text: u.EmailAddress,
          }))
        );
      })
      .finally(() => setLoading(false));
  }, [props.customerId]);

  return (
    <ComboBox
      label={props.label ?? "User"}
      options={options}
      selectedKey={props.selectedUserId ?? null}
      onChange={(_, option) =>
        props.onChange(option ? (option.key as number) : undefined)
      }
      allowFreeform
      autoComplete="on"
      disabled={props.disabled || loading}
      placeholder={
        loading
          ? "Loading users..."
          : props.placeholder ?? "Search user..."
      }
      errorMessage={props.errorMessage}
    />
  );
};

export default UserComboBox;

export const findUserEmail = (
  customerId: number,
  userId: number
): string | undefined =>
  userCache.get(customerId)?.find((u) => u.Id === userId)?.EmailAddress;
